import { ColorNordicGrey4 } from 'autogen/design-tokens/tokens';
import type { FlowBoxModel } from 'autogen/swagger/all';
import {
    AcceptFunctionalCookiesToSeeElementBody,
    AcceptCookiesToSeeElementHeadline
} from 'autogen/translation-keys/trans-website-shared';
import CookieInfobox from 'components/CookieInfobox';
import useHasInformationCookieBeenAccepted from 'hooks/user/useHasInformationCookieBeenAccepted';
import { CookieCategory } from 'modules/helpers/cookieInformationHelper';
import Spinner from 'modules/shared/circleSpinner';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export type FlowboxProps = {
    model: FlowBoxModel;
    productId: number;
};

const Container = styled.div<{ $loading?: boolean; $showBackground?: boolean }>`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 335px;
    opacity: ${({ $loading }) => ($loading ? 0.4 : 1)};
    ${({ $showBackground }) => $showBackground && `background-color: ${ColorNordicGrey4}`};
`;

const FlowboxElement = styled.div`
    width: 100%;
    height: 100%;
`;

const StyledSpinner = styled(Spinner)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

function Flowbox(props: FlowboxProps) {
    const { model, productId } = props;
    const { key, locale } = model;
    const flowboxRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const isFunctionalCookieAccepted = useHasInformationCookieBeenAccepted(CookieCategory.functional);
    const { t } = useTranslation();

    const loadFlowboxScript = () => {
        const existingScript = document.getElementById('flowbox-js-embed');

        if (!existingScript) {
            if (!window.flowbox) {
                const f = (...args) => {
                    f.q.push(args);
                };
                f.q = [];
                window.flowbox = f;
            }

            const script = document.createElement('script');
            script.id = 'flowbox-js-embed';
            script.src = 'https://connect.getflowbox.com/flowbox.js';
            script.async = true;
            document.head.appendChild(script);
        }
    };

    const initFlowbox = async () => {
        await loadFlowboxScript();

        if (window.flowbox) {
            window.flowbox('init', {
                container: flowboxRef.current,
                key: key,
                locale: locale,
                productId: productId
            });

            setLoading(false);
        }
    };

    useEffect(() => {
        if (isFunctionalCookieAccepted) {
            initFlowbox();
        }
    }, [isFunctionalCookieAccepted]);

    return isFunctionalCookieAccepted ? (
        <Container $loading={loading}>
            <FlowboxElement ref={flowboxRef} />
            {loading && <StyledSpinner size={18} />}
        </Container>
    ) : (
        <Container $showBackground={true}>
            <CookieInfobox
                headline={t(AcceptCookiesToSeeElementHeadline)}
                body={t(AcceptFunctionalCookiesToSeeElementBody)}
            />
        </Container>
    );
}

export default Flowbox;
